// owner/screens/Finance.js — «المالية» (/finance). No mock-up of its own; built in the approved owner kit, as the
// overview (tasks/owner-mobile-app-build.md §10 "8c batch 3", approved by Adil 4 Nov 2026):
//   - four fixed periods from the server's presets (this month, last month, last 6 months, this year); no custom
//     range, no date maths here — "to date" is the server's `inProgress`;
//   - revenue (labelled without «قبل الضريبة», Adil 4 Nov), the booking sites' fee and «الصافي بعد عمولة منصة الحجز»:
//     never Host Me's own fee, which
//     is not deducted here (the label says so); a fee the booking site did not send is an estimate, marked «تقديري»;
//   - occupancy, bookings, average night price; the same split per booking site, without a fee percentage;
//   - the monthly reports, newest first; one opens on screen (/finance/reports/:id).
// Not carried over from the shared page: % change, highest/lowest rate, stay length, lead time, check-ins by weekday.

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useLanguage from '../../hooks/useLanguage';
import { fill } from '../../utils/localeText';
import Icon from '../ui/Icon';
import PlatformMark from '../ui/PlatformMark';
import UnitPicker from '../ui/UnitPicker';
import { SkeletonBlocks, ErrorState, InlineError, EmptyState } from '../ui/States';
import useOwnerFormat from '../ui/useOwnerFormat';
import { useOwnerUnits, usePortfolio, useOwnerReports } from '../api/ownerReads';

const PERIODS = ['thisMonth', 'lastMonth', 'last6Months', 'thisYear'];

const SectionError = ({ text, onRetry }) => <div className="o-sheet"><InlineError text={text} onRetry={onRetry} /></div>;

// The tag says «تقديري»; what it means is written under the fee total (a hover title never reaches a phone).
const EstimateTag = () => {
  const { t } = useLanguage();
  return <span className="o-vat-tag o-estimate">{t('ownerApp.finance.estimate')}</span>;
};

/** One amount with its currency; `sign` for a deduction. */
const Amount = ({ value, sign = '' }) => {
  const f = useOwnerFormat();
  return <span className="o-v"><span className="o-money">{`${sign}${f.amount(value)}`}</span> <span className="o-cur">{f.currency}</span></span>;
};

const Figures = ({ query, period }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  if (query.isLoading) return <div className="o-sk o-sk-hero" aria-busy="true" />;
  if (query.isError || !query.data?.metrics) {
    return <section className="o-section"><SectionError text={t('ownerApp.finance.loadError')} onRetry={query.refetch} /></section>;
  }
  const { metrics, dateRange } = query.data;
  const periodName = t(`ownerApp.finance.periods.${period}`);
  return (
    <section className="o-hero" aria-label={`${t('ownerApp.finance.revenue')} — ${periodName}`}>
      <svg className="o-house-bg" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M3 10.6 10.9 4.5a1.8 1.8 0 0 1 2.2 0L21 10.6V18.5a2.5 2.5 0 0 1-2.5 2.5h-13A2.5 2.5 0 0 1 3 18.5Z" />
      </svg>
      <p className="o-label">
        {t('ownerApp.finance.revenue')} · <span className="o-month">{periodName}</span>
        {dateRange?.inProgress && ` ${t('ownerApp.finance.toDate')}`}
      </p>
      <p className="o-value"><span className="o-money">{f.amount(metrics.revenue?.gross ?? 0)}</span><span className="o-cur">{f.currency}</span></p>
      <div className="o-stats">
        <div className="o-stat">
          <p className="o-n"><span className="o-money">{f.percent(metrics.occupancy?.rate ?? 0)}</span></p>
          <p className="o-l">{t('ownerApp.overview.occupancy')}</p>
        </div>
        <div className="o-stat">
          <p className="o-n"><span className="o-money">{f.count(metrics.bookings?.total ?? 0)}</span></p>
          <p className="o-l">{t('ownerApp.finance.bookings')}</p>
        </div>
        <div className="o-stat">
          <p className="o-n"><span className="o-money">{f.amount(metrics.rates?.adr ?? 0)}</span></p>
          <p className="o-l">{fill(t('ownerApp.finance.averageNight'), { currency: f.currency })}</p>
        </div>
      </div>
    </section>
  );
};

const Net = ({ query }) => {
  const { t } = useLanguage();
  const revenue = query.data?.metrics?.revenue;
  if (!revenue) return null;  // loading and errors are shown once, by Figures
  return (
    <section className="o-section">
      <div className="o-section-head"><h2>{t('ownerApp.finance.netTitle')}</h2></div>
      <div className="o-sheet">
        {revenue.gross > 0 ? (
          <ul className="o-moneyblock">
            <li><span className="o-k">{t('ownerApp.finance.revenue')}</span><Amount value={revenue.gross} /></li>
            <li className="o-fee">
              <span className="o-k">
                {t('ownerApp.finance.siteFees')}
                {revenue.platformCommissionsEstimated && <EstimateTag />}
              </span>
              <Amount value={revenue.platformCommissions} sign="−" />
            </li>
            <li className="o-net"><span className="o-k">{t('ownerApp.common.netAfterSiteFee')}</span><Amount value={revenue.net} /></li>
          </ul>
        ) : (
          <p className="o-empty-row">{t('ownerApp.finance.noRevenue')}</p>
        )}
      </div>
      {revenue.gross > 0 && revenue.platformCommissionsEstimated && (
        <p className="o-footnote"><EstimateTag /> {t('ownerApp.finance.estimateHint')}</p>
      )}
    </section>
  );
};

const BySite = ({ query }) => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const rows = (query.data?.metrics?.byPlatform || [])
    .filter((p) => p.grossRevenue > 0)
    .sort((a, b) => b.grossRevenue - a.grossRevenue);
  if (rows.length === 0) return null;  // nothing earned: the net card already says so
  return (
    <section className="o-section">
      <div className="o-section-head"><h2>{t('ownerApp.finance.bySite')}</h2></div>
      <ul className="o-sheet o-rows o-site-rows">
        {rows.map((p) => (
          <li key={p.platform} className="o-site">
            <div className="o-site-head">
              <PlatformMark platform={p.platform} withName />
              <span className="o-aside">{f.bookings(p.bookings)}</span>
            </div>
            <dl className="o-site-figs">
              <div><dt>{t('ownerApp.finance.revenueShort')}</dt><dd className="o-money">{f.amount(p.grossRevenue)}</dd></div>
              <div>
                <dt>{t('ownerApp.finance.feeShort')}{p.commissionEstimated && <EstimateTag />}</dt>
                <dd className="o-money">{p.commission > 0 ? `−${f.amount(p.commission)}` : f.amount(0)}</dd>
              </div>
              <div className="o-site-net"><dt>{t('ownerApp.finance.netShort')}</dt><dd className="o-money">{f.amount(p.netRevenue)}</dd></div>
            </dl>
          </li>
        ))}
      </ul>
      <p className="o-footnote">{t('ownerApp.finance.amountsIn')}</p>
    </section>
  );
};

const Reports = () => {
  const { t } = useLanguage();
  const f = useOwnerFormat();
  const reports = useOwnerReports();
  return (
    <section className="o-section o-section-last">
      <div className="o-section-head"><h2>{t('ownerApp.finance.reportsTitle')}</h2></div>
      {reports.isLoading && <div className="o-sk" style={{ height: 120, borderRadius: 20 }} aria-busy="true" />}
      {reports.isError && <SectionError text={t('ownerApp.finance.reportsError')} onRetry={reports.refetch} />}
      {reports.data && (
        reports.data.length === 0 ? (
          <div className="o-sheet">
            <p className="o-empty-row">{t('ownerApp.finance.noReports')}</p>
          </div>
        ) : (
          <ul className="o-sheet o-rows">
            {reports.data.map((r) => (
              <li key={r._id}>
                <Link to={`/finance/reports/${r._id}`} className="o-row">
                  <div className="o-body">
                    <p className="o-t">{f.month(r.periodMonth)}</p>
                    <p className="o-m">{f.units(r.propertyCount || 0)}</p>
                  </div>
                  <div className="o-end o-muted"><Icon name="chevEnd" size="sm" /></div>
                </Link>
              </li>
            ))}
          </ul>
        )
      )}
    </section>
  );
};

const Finance = () => {
  const { t } = useLanguage();
  const [unitId, setUnitId] = useState('');  // '' = all units
  const [period, setPeriod] = useState('thisMonth');
  const units = useOwnerUnits();
  const figures = usePortfolio(period, unitId);
  const unitList = useMemo(() => (Array.isArray(units.data) ? units.data : []), [units.data]);

  if (units.isLoading) return <SkeletonBlocks hero blocks={3} />;
  if (units.isError) return <ErrorState title={t('ownerApp.overview.loadError')} onRetry={units.refetch} />;
  if (unitList.length === 0) {
    return <EmptyState icon="units" title={t('ownerApp.overview.noUnitsTitle')} hint={t('ownerApp.overview.noUnitsHint')} />;
  }

  return (
    <>
      <div className="o-page-title">
        <h1>{t('ownerApp.finance.title')}</h1>
        <span className="o-underline" />
      </div>
      {unitList.length > 1 && <UnitPicker units={unitList} value={unitId} onChange={setUnitId} />}
      <div className="o-seg" role="group" aria-label={t('ownerApp.finance.period')}>
        {PERIODS.map((key) => (
          <button key={key} type="button" aria-pressed={period === key} onClick={() => setPeriod(key)}>
            {t(`ownerApp.finance.periods.${key}`)}
          </button>
        ))}
      </div>
      <div className="o-overview-grid">
        <div className="o-col">
          <Figures query={figures} period={period} />
          <Net query={figures} />
        </div>
        <div className="o-col">
          <BySite query={figures} />
        </div>
      </div>
      <Reports />
    </>
  );
};

export default Finance;
